// Pure derivation of display-ready session state from the raw Firebase data.
// Shared by useSession (live) and the archive viewer (static snapshots).

import { fuzzyMatch, normalize } from './matching';

export const CDM_COLUMNS = ['diagnoses', 'supporting', 'opposing'];
export const DIFF_COLUMNS = ['etiologicalClass', 'diseases', 'supporting', 'opposing'];

// Guided case flow: brainstorm boards live under fixed pseudo-row ids so they
// reuse the whole items/votes machinery.
export const HISTORY_ROW_ID = '_history';
export const PHYSICAL_ROW_ID = '_physical';
export const LABS_ROW_ID = '_labs';
export const HISTORY_COLUMNS = [
  { id: 'hpi', label: 'HPI' },
  { id: 'pmh', label: 'PMH / Meds / Allergies' },
  { id: 'famsoc', label: 'Family / Social' },
  { id: 'ros', label: 'ROS' },
];
export const PHYSICAL_COLUMNS = [
  { id: 'vitals', label: 'Vitals' },
  { id: 'general', label: 'General / Inspection' },
  { id: 'heent', label: 'HEENT / Neck' },
  { id: 'cardio', label: 'Cardiovascular' },
  { id: 'resp', label: 'Respiratory' },
  { id: 'abdo', label: 'Abdominal' },
  { id: 'neuro', label: 'Neurological' },
  { id: 'msk', label: 'MSK / Skin / Other' },
];
export const LABS_COLUMNS = [
  { id: 'bedside', label: 'Bedside / POC' },
  { id: 'bloods', label: 'Bloods' },
  { id: 'imaging', label: 'Imaging' },
  { id: 'special', label: 'Special / Other' },
];

export const STEP_ORDER = ['history', 'physical', 'differential', 'labs', 'cdm'];
export const STEP_LABELS = {
  history: 'History', physical: 'Physical', differential: 'Differential', labs: 'Labs', cdm: 'CDM',
};
export const stepIndex = (step) => STEP_ORDER.indexOf(step);

// Board tabs (the ones that use the brainstorm/reveal machinery).
export const BOARD_TABS = {
  history: { rowId: HISTORY_ROW_ID, columns: HISTORY_COLUMNS },
  physical: { rowId: PHYSICAL_ROW_ID, columns: PHYSICAL_COLUMNS },
  labs: { rowId: LABS_ROW_ID, columns: LABS_COLUMNS },
};

const toArr = (node) =>
  Object.entries(node || {}).map(([id, v]) => ({ id, ...v })).sort((a, b) => a.id.localeCompare(b.id));

// Labs moved from caseFlow/labs to caseFlow/findings/labs when Labs became its
// own step, and used {name,value} instead of {text,detail}. Sessions loaded
// before that change must keep working, or their labs board renders empty.
const toLabs = (cf) =>
  toArr(cf.findings?.labs || cf.labs).map((l) => ({
    ...l,
    text: l.text || l.name,
    detail: l.detail || l.value || '',
    category: l.category || 'bloods',
  }));

// Diagnostic stream (case variant) metadata, when the case has any.
const toStream = (cf) => {
  const s = cf?.stream;
  if (!s || !s.id) return null;
  const queue = Array.isArray(s.queue) ? s.queue : [];
  const index = typeof s.index === 'number' ? s.index : 0;
  return {
    id: s.id,
    label: s.label || s.id,
    summary: s.summary || '',
    queue,
    index,
    position: index + 1,
    total: queue.length || 1,
    nextId: index + 1 < queue.length ? queue[index + 1] : null,
  };
};

export const getCaseFlow = (data) => {
  const cf = data?.caseFlow;
  if (!cf || cf.active === false) {
    return {
      active: false, step: null, completed: {}, labsRevealedAt: null, diagnosisRevealedAt: null,
      findings: { history: [], physical: [], labs: [] },
      labs: [], diagnosis: null, cdmAnswer: [], diffAnswer: [], overrides: {}, stream: null,
      problemRepresentation: {}, classGaps: {},
    };
  }
  return {
    active: true,
    caseId: cf.caseId || 'custom',
    sourceCaseId: cf.sourceCaseId || cf.caseId || null,
    stream: toStream(cf),
    title: cf.title || 'Case',
    presentingSymptom: cf.presentingSymptom || '',
    demographics: cf.demographics || '',
    step: STEP_ORDER.includes(cf.step) ? cf.step : 'history',
    completed: cf.completed || {},
    labsRevealedAt: cf.completed?.labs || cf.labsRevealedAt || null,
    diagnosisRevealedAt: cf.diagnosisRevealedAt || null,
    findings: {
      history: toArr(cf.findings?.history),
      physical: toArr(cf.findings?.physical),
      labs: toLabs(cf),
    },
    // Labs live in findings.labs (so they reuse the board machinery); this
    // alias keeps the results panel, summary and exports on one source.
    labs: toLabs(cf),
    diagnosis: cf.diagnosis || null,
    cdmAnswer: toArr(cf.cdmAnswer),
    diffAnswer: toArr(cf.diffAnswer),
    overrides: cf.overrides || {},
    // Authored per-step restatements of the case (see mockCases.js).
    problemRepresentation: cf.problemRepresentation || {},
    // What the room collectively missed, written by the facilitator's client
    // at each merge or reveal: { [scope]: { total, computedAt, items[] } }.
    classGaps: Object.fromEntries(Object.entries(cf.classGaps || {}).map(([scope, g]) => [scope, {
      total: g?.total || 0,
      computedAt: g?.computedAt || null,
      items: Array.isArray(g?.items) ? g.items : Object.values(g?.items || {}),
    }])),
  };
};

/**
 * The step whose problem representation should be on screen: the latest
 * revealed one. CDM counts only once the diagnosis is revealed, since its
 * statement names the answer.
 */
export const problemRepresentationStep = (cf) => {
  if (!cf?.active) return null;
  const revealed = STEP_ORDER.filter((s) => (s === 'cdm' ? !!cf.diagnosisRevealedAt : !!cf.completed?.[s]));
  return [...revealed].reverse().find((s) => (cf.problemRepresentation?.[s] || '').trim()) || null;
};

export const DEFAULT_SETTINGS = {
  voteMode: 'cumulative', // 'cumulative' | 'single' | 'budget'
  voteBudget: 10,         // only used in budget mode
  hideVotes: false,       // blind voting: students don't see totals until reveal
  probabilityMode: false, // probability sliders on CDM diagnoses
  phase: 'group',         // 'group' | 'individual' (think-alone-then-share)
  // Answer each entry the moment it's added, instead of waiting for the
  // facilitator's reveal. Only affects entries the class actually made, 
  // the "not elicited" list still waits for the reveal.
  instantAnswers: false,
};

export const getSettings = (data) => ({ ...DEFAULT_SETTINGS, ...(data?.settings || {}) });

// Vote leaves are { name, value } (v2) or a bare number (legacy sessions).
export const voteVal = (v) =>
  typeof v === 'number' ? v : (v && typeof v.value === 'number' ? v.value : 0);

const probVal = (p) => (typeof p === 'number' ? p : (p && typeof p.value === 'number' ? p.value : null));

const median = (values) => {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : Math.round((s[mid - 1] + s[mid]) / 2);
};

// Individual phase: my own private entries render in the very same columns as
// shared items, marked `private` so the UI can label them and skip voting.
// They live under individual/$identityKey (rules-private), never under items/.
const buildPrivate = (privateNode, identityKey) =>
  Object.entries(privateNode || {}).map(([id, entry]) => ({
    id,
    ...entry,
    addedByKey: identityKey,
    private: true,
    voters: {},
    votes: 0,
    myVote: 0,
  }));

// A merged card counts every student beyond the first who listed it as one
// thumbs up: "ATN" from four students publishes as one card scoring 3 before
// anyone votes. `votes` is that net score (leaves plus the merge), so ranking,
// tints, exports and the timeline all see it; `voteLeaves` is the raw tally.
const mergeBonus = (item) => Math.max(0, (Number(item?.listedBy) || 0) - 1);

const buildColumn = (itemsNode = {}, votesNode = {}, probsNode, identityKey, privateNode = null, ranked = false) =>
  Object.entries(itemsNode)
    .map(([id, item]) => {
      const voters = votesNode[id] || {};
      const voteLeaves = Object.values(voters).reduce((a, v) => a + voteVal(v), 0);
      const mergeVotes = mergeBonus(item);
      const votes = voteLeaves + mergeVotes;
      const out = { id, ...item, voters, votes, voteLeaves, mergeVotes, myVote: voteVal(voters[identityKey]) };
      if (probsNode) {
        const entries = probsNode[id] || {};
        const values = Object.values(entries).map(probVal).filter((v) => v !== null);
        out.prob = {
          n: values.length,
          median: median(values),
          min: values.length ? Math.min(...values) : null,
          max: values.length ? Math.max(...values) : null,
          mine: probVal(entries[identityKey]),
        };
      }
      return out;
    })
    .concat(buildPrivate(privateNode, identityKey))
    // Within a cell, net score ranks the cards once votes are visible; blind
    // voting keeps arrival order so the layout can't leak the tally.
    .sort((a, b) => (ranked ? (b.votes || 0) - (a.votes || 0) : 0) || (a.createdAt || 0) - (b.createdAt || 0));

/**
 * `privateNode` is the caller's OWN individual/$identityKey subtree
 * ({ [rowId]: { [column]: { [entryId]: entry } }, _gaps }), or null. During the
 * individual phase its entries are merged into the same columns as shared
 * items; the facilitator passes their own (empty) node, so their screen shows
 * nothing until the merge.
 */
export function deriveSession(data, identityKey, privateNode = null) {
  const settings = getSettings(data);
  const meta = data?.meta || {};
  const isFacilitator = !!meta.facilitatorKey && meta.facilitatorKey === identityKey;
  // Blind voting: facilitator always sees totals; students only after reveal.
  const revealed = isFacilitator || !settings.hideVotes;

  const items = data?.items || {};
  const votes = data?.votes || {};
  const probs = data?.probabilities || {};
  // Private entries only render while the phase is live; after the merge they
  // exist as shared items and the leftovers are the student's saved gaps.
  const mine = settings.phase === 'individual' ? (privateNode || {}) : {};

  const makeRow = (id, row, columns) => {
    const out = { id, ...row };
    columns.forEach((c) => {
      out[c] = buildColumn(
        items[id]?.[c],
        votes[id]?.[c],
        c === 'diagnoses' ? (probs[id] || {}) : null,
        identityKey,
        mine[id]?.[c],
        revealed
      );
    });
    return out;
  };

  const rowScore = (row) => {
    if (settings.probabilityMode) {
      const meds = row.diagnoses.map((d) => d.prob?.median).filter((m) => m !== null && m !== undefined);
      return meds.length ? Math.max(...meds) : 0;
    }
    // Net score of the row's best diagnosis; a downvoted-only row sorts
    // below an untouched one, so the sign is kept.
    return row.diagnoses.length ? Math.max(...row.diagnoses.map((d) => d.votes)) : 0;
  };

  const byCreated = (a, b) => (a.createdAt || 0) - (b.createdAt || 0);
  const all = Object.entries(data?.rows || {}).map(([id, row]) => makeRow(id, row, CDM_COLUMNS));
  const regular = all
    .filter((r) => !r.isCritical)
    // While votes are hidden, students see a stable createdAt order so the
    // row order itself can't leak the tally. Reveal triggers the FLIP re-sort.
    .sort(revealed ? (a, b) => rowScore(b) - rowScore(a) || byCreated(a, b) : byCreated)
    .map((r, i) => ({ ...r, displayOrder: String(i + 1) }));
  const critical = all
    .filter((r) => r.isCritical)
    .sort(byCreated)
    .map((r, i) => ({ ...r, displayOrder: `Critical Dx ${i + 1}` }));

  const diffScore = (row) => (row.diseases.length ? Math.max(...row.diseases.map((d) => d.votes)) : 0);
  const diffRows = Object.entries(data?.diffRows || {})
    .map(([id, row]) => makeRow(id, row, DIFF_COLUMNS))
    .sort(revealed ? (a, b) => diffScore(b) - diffScore(a) || byCreated(a, b) : byCreated);

  const onlineUsers = [...new Set(Object.values(data?.presence || {}).map((p) => p.name).filter(Boolean))];

  const cdmRows = [...regular, ...critical];

  // ---- Guided case flow ----
  const caseFlow = getCaseFlow(data);

  // Brainstorm boards (History / Physical). After a step is revealed, each
  // entry is matched against the case's findings, facilitator overrides win.
  const buildBoard = (rowKey, columns, tab) => {
    const board = {
      columns: {}, missedFindings: [], summaryDetails: [], revealed: false, showMissed: false,
    };
    // Within a category, net score ranks the cards (ties keep arrival order);
    // under blind voting the order stays createdAt so it can't leak the tally.
    const byNet = revealed ? (a, b) => (b.votes || 0) - (a.votes || 0) || byCreated(a, b) : byCreated;
    columns.forEach((c) => {
      board.columns[c.id] = buildColumn(
        items[rowKey]?.[c.id], votes[rowKey]?.[c.id], null, identityKey, mine[rowKey]?.[c.id]
      ).sort(byNet);
    });
    const stepDone = !!caseFlow.completed[tab];
    // Instant mode answers entries as they're added; the un-elicited findings
    // are still held back until the facilitator reveals the step.
    if (!caseFlow.active || !(stepDone || settings.instantAnswers)) return board;

    board.revealed = true;
    board.showMissed = stepDone;
    const findings = caseFlow.findings[tab] || [];
    const firstCol = columns[0].id;
    // Findings with no/unknown category fall back to the first column, so a
    // finding can never vanish from the board.
    const sectionOf = (f) => (columns.some((c) => c.id === f.category) ? f.category : firstCol);
    const matchedIds = new Set();

    columns.forEach((c) => board.columns[c.id].forEach((item) => {
      const override = caseFlow.overrides[item.id];
      // Where the entry is actually stored, mutations must keep targeting
      // this column even if the card is displayed in another one below.
      item.sourceColumn = c.id;
      item.targetSection = c.id;
      // Match within the entry's own section first, a broad entry ("vitals")
      // legitimately covers every finding it hits, not just the first one.
      // tokens: true, students phrase questions freely; section scoping keeps
      // the looser matching from reaching across the board.
      let hits = findings.filter((f) => sectionOf(f) === c.id && fuzzyMatch(item.text, f, { tokens: true }));
      if (!hits.length) {
        // Right answer filed in the wrong box: keep the credit, and relocate
        // the card to the section the finding actually belongs to. Only ONE
        // finding can be picked here, so take a precise (alias/containment)
        // match ahead of a loose token one, otherwise "chest pain" lands on
        // whichever finding happens to share a word first.
        const elsewhere = (opts) => findings.find((f) => sectionOf(f) !== c.id && fuzzyMatch(item.text, f, opts));
        const other = elsewhere(undefined) || elsewhere({ tokens: true });
        if (other) {
          hits = [other];
          item.targetSection = sectionOf(other);
        }
      }
      const isMatch = override === true ? true : override === false ? false : hits.length > 0;
      item.matchState = isMatch ? 'matched' : 'unmatched';
      item.matchDetails = isMatch
        ? hits.map((f) => ({ name: f.text, text: f.detail || f.text, flag: f.flag || null }))
        : [];
      // A shorthand entry ("troponin") is promoted to the case's canonical
      // finding name ("Troponin") once revealed. Only when it resolves to a
      // single finding, a broad entry like "vitals" covers several, so it
      // keeps the student's wording and labels each result instead.
      item.matchTitle = isMatch && hits.length === 1 ? hits[0].text : null;
      item.hasOverride = override !== undefined;
      if (!isMatch) item.targetSection = c.id; // an overridden-off match doesn't move
      if (isMatch) hits.forEach((f) => matchedIds.add(f.id));
    }));

    // Relocate matched-but-misfiled entries into the section they belong to.
    const relocating = [];
    columns.forEach((c) => {
      board.columns[c.id] = board.columns[c.id].filter((item) => {
        if (item.targetSection && item.targetSection !== c.id) {
          relocating.push(item);
          return false;
        }
        return true;
      });
    });
    relocating.forEach((item) => board.columns[item.targetSection]?.push(item));
    columns.forEach((c) => board.columns[c.id].sort(byNet));

    board.missedFindings = board.showMissed
      ? findings.filter((f) => !matchedIds.has(f.id)).map((f) => ({ ...f, section: sectionOf(f) }))
      : [];
    // Everything the class now knows. Before the step is completed that's only
    // what they actually elicited; after it, every finding is on the board.
    board.summaryDetails = (board.showMissed ? findings : findings.filter((f) => matchedIds.has(f.id)))
      .map((f) => f.detail || f.text)
      .filter(Boolean);
    return board;
  };
  const historyBoard = buildBoard(HISTORY_ROW_ID, HISTORY_COLUMNS, 'history');
  const physicalBoard = buildBoard(PHYSICAL_ROW_ID, PHYSICAL_COLUMNS, 'physical');
  const labsBoard = buildBoard(LABS_ROW_ID, LABS_COLUMNS, 'labs');

  // After the final reveal, flag which proposed diagnoses hit the answer.
  if (caseFlow.active && caseFlow.diagnosisRevealedAt && caseFlow.diagnosis) {
    cdmRows.forEach((row) => (row.diagnoses || []).forEach((item) => {
      item.correctDx = fuzzyMatch(item.text, caseFlow.diagnosis);
    }));
  }

  // Budget accounting: total |myVote| across every item in the session.
  let spentVotes = 0;
  const tally = (rows, cols) =>
    rows.forEach((r) => cols.forEach((c) => (r[c] || []).forEach((it) => { spentVotes += Math.abs(it.myVote || 0); })));
  tally(cdmRows, CDM_COLUMNS);
  tally(diffRows, DIFF_COLUMNS);
  [historyBoard, physicalBoard, labsBoard].forEach((b) =>
    Object.values(b.columns).forEach((col) => col.forEach((it) => { spentVotes += Math.abs(it.myVote || 0); })));

  return {
    settings, meta, isFacilitator, revealed, cdmRows, diffRows, onlineUsers, spentVotes,
    caseFlow, historyBoard, physicalBoard, labsBoard,
  };
}

// Case stages in presentation order.
export const getStages = (data) =>
  Object.entries(data?.case?.stages || {})
    .map(([id, s]) => ({ id, ...s }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.createdAt || 0) - (b.createdAt || 0));

// Snapshots oldest-first.
export const getSnapshots = (data) =>
  Object.entries(data?.snapshots || {})
    .map(([id, s]) => ({ id, ...s, ranking: s.ranking || [] }))
    .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

// Ranked top diagnosis per regular row (rows arrive already display-sorted).
export function computeRanking(cdmRows, probabilityMode) {
  const metric = (d) => (probabilityMode ? (d.prob?.median ?? -1) : (d.votes || 0));
  return cdmRows
    .filter((r) => !r.isCritical)
    .map((r) => {
      const top = [...(r.diagnoses || [])].sort((a, b) => metric(b) - metric(a))[0];
      return top ? { text: top.text, score: Math.max(0, metric(top)) } : null;
    })
    .filter(Boolean);
}

// Individual-phase scopes, in teaching order. A gap record is written per
// scope at merge time so each student keeps their own list of what they missed.
export const GAP_SCOPES = ['history', 'physical', 'differential', 'labs', 'cdm'];
export const GAP_LABELS = {
  history: 'History', physical: 'Physical', differential: 'Differential', labs: 'Labs', cdm: 'CDM grid',
};

// My saved individual-phase gaps (survives the merge; shown at the debrief).
export const getMyGaps = (privateNode) =>
  Object.entries(privateNode?._gaps || {})
    .map(([scope, g]) => ({
      scope,
      label: g?.label || GAP_LABELS[scope] || scope,
      mergedAt: g?.mergedAt || 0,
      mineCount: g?.mineCount || 0,
      peers: Object.values(g?.peers || {}),
      findings: Object.values(g?.findings || {}),
    }))
    .filter((g) => g.peers.length || g.findings.length)
    .sort((a, b) => {
      const ai = GAP_SCOPES.indexOf(a.scope);
      const bi = GAP_SCOPES.indexOf(b.scope);
      return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi) || a.mergedAt - b.mergedAt;
    });

// Normalization used to collapse duplicate diagnoses when merging
// individual-phase entries ("PE" ≠ "pe." ≠ " Pe " should all merge).
export const normalizeDx = (text) => normalize(text);
